import { Form } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { ConfirmationModal, showAlertNotification, TableCommon } from 'components/common';
import ModalConfirm from 'components/common/Modal/ModalConfirmCommon';
import { useAppDispatch, useAppSelector } from 'hooks';
import { ProductImage, Supplier } from 'models';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCreateCodeMutation,
  useCreateProductMutation,
  useDeleteProductMutation,
  useGetProductsQuery,
  useLazyGetProductByIdQuery,
} from 'services';
import { setTableParams, TableCacheItem } from 'stores/slices/tableCacheSlice';
import { Attribute, ProductColumn, ProductPayload, ProductSalePrice, QueryParams, SelectOption, Unit } from 'types';
import { formatPrice, generateRandomBarcode } from 'utils';
import { buildFullParams, parseSimpleFilter } from 'utils/filterBuilder';
import { initializeFormData } from 'utils/productHelpers';

interface ProductTableProps {
  newColumns: ColumnsType<ProductColumn>;
  draftId: string;
  idSupplier?: string; // Make idSupplier optional
  onParentFilter?: (query: QueryParams) => void;
  onGetFilteredQuerySetter?: (setter: React.Dispatch<React.SetStateAction<QueryParams>>) => void;
}
const ProductTable = ({
  newColumns,
  draftId,
  idSupplier,
  onParentFilter,
  onGetFilteredQuerySetter,
}: ProductTableProps) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [deleteProduct] = useDeleteProductMutation();
  const [createProduct, { isLoading: createLoading }] = useCreateProductMutation();
  const [triggerGetByIdProduct, { isLoading: triggerLoading }] = useLazyGetProductByIdQuery();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [productChoice, setChoiceProduct] = useState<ProductColumn | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isPerformingOperation, setIsPerformingOperation] = useState(false);
  const [createcode, { data: code }] = useCreateCodeMutation();
  const [initialProductUnits, setInitialProductUnits] = useState<Unit[]>([]);
  const [initialProductAttributes, setInitialProductAttributes] = useState<Attribute[]>([]);
  const [initialProductSalePrices, setInitialProductSalePrices] = useState<ProductSalePrice[]>([]);
  const [initalProductSuppliers, setInitialProductSuppliers] = useState<Supplier[]>([]);
  const [initialImage, setInitialImage] = useState<string>('');
  const [statusToCopy, setStatusToCopy] = useState<string>('Active');
  const [isInitalCopy, setIsInitalCopy] = useState<boolean>(false);
  const [nameData, setNameData] = useState<{
    categoryName: string;
    brandName: string;
    baseUnitName: string;
    currencyUnitName: string;
  }>({
    categoryName: '',
    brandName: '',
    baseUnitName: '',
    currencyUnitName: '',
  });
  const [products, setProducts] = useState<
    (ProductColumn & {
      dataSelect: SelectOption[];
    })[]
  >([]);
  const pageKey = 'product-list';
  const dispatch = useAppDispatch();
  const tableState = useAppSelector((s) => s.tableCache[pageKey]) as TableCacheItem<ProductColumn> | undefined;
  const [filteredQuery, setFilteredQuery] = useState<QueryParams>({
    size: tableState?.pageSize ?? 10,
    page: tableState?.page ?? 1,
    sort: tableState?.sortField
      ? `${String(tableState.sortField)}:${tableState.sortOrder === 'descend' ? 'desc' : 'asc'}`
      : 'updatedAt:desc',
    searchKey: 'name|color|dimension|sizeSpec|product.code',
    searchValue: tableState?.search ?? '',
    filter: tableState?.filter ?? 'isActive:eq:true',
    include: 'product.productVersions|category|brand|status|productUnits.unit|currencyUnit|baseUnit',
  });
  const { data, isLoading, isError } = useGetProductsQuery({
    query: {
      filter: 'isActive:eq:true',
      sort: 'updatedAt:desc',
      ...(idSupplier && { supplierId: idSupplier }),
      ...filteredQuery,
    } as QueryParams,
  });
  const initDataProduct = () => {
    try {
      const mapData = data?.data?.rows.flatMap((item) => {
        const productVersions = item.product?.productVersions?.flatMap((productVersion) => {
          return {
            value: productVersion.version?.toString() ?? '',
            label: 'Ver ' + (productVersion.version?.toString() ?? ''),
          };
        }) as SelectOption[];

        return {
          key: item.id ? item.id : '',
          ['product.code']: item?.product?.code || '',
          fullName:
            [item?.name, item?.color, item?.dimension, item.sizeSpec]
              .filter((item) => item !== undefined && item !== null && item !== '')
              .join(' - ') || '',
          name:
            [item?.name, item?.color, item?.dimension, item.sizeSpec]
              .filter((item) => item !== undefined && item !== null && item !== '')
              .join(' - ') || '',
          version: item?.version || '',
          ['category.name']: item.category?.name || '',
          ['brand.name']: item.brand?.name || '',
          categoryId: item.categoryId || '',
          ['product.quantity']: item.product?.quantity || 0,
          stock: item.product?.quantity || 0,
          ['status.name']: item.status?.name || '',

          ['status.id']: item.status?.id || '',
          salePrice: item.salePrice ? [formatPrice(item.salePrice), item?.currencyUnit?.name].join(' ') : '',
          costPrice: item.costPrice ? [formatPrice(item.costPrice), item?.currencyUnit?.name].join(' ') : '',
          ['product.barcode']: item.product?.barcode || '',
          ['baseUnit.name']: item.baseUnit?.name,
          dataSelect: productVersions || [],
          productId: item.productId || '',
        };
      });
      setProducts(mapData || []);
      dispatch(
        setTableParams({
          pageKey,
          params: {
            lastFetched: Date.now(),
            search: filteredQuery.searchValue ?? '',
            page: filteredQuery.page ?? 1,
            pageSize: filteredQuery.size ?? 10,
            filter: filteredQuery.filter ?? '',
          } as unknown as Partial<TableCacheItem<unknown>>,
        }),
      );
    } catch (error) {
      console.log('initDataProduct: ', error);
    }
  };
  useEffect(() => {
    if (data && !isError) {
      initDataProduct();
    }
  }, [data]);
  // Type-safe action handlers
  const handleEdit = (product: ProductColumn): void => {
    navigate(`/items/products/detail-product/${product.productId}/${product.key}`);
  };
  const handleDelete = (product: ProductColumn) => {
    setChoiceProduct(product);
    setIsDeleteModalOpen(true);
  };
  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setChoiceProduct(null);
  };
  const confirmDelete = async () => {
    if (!productChoice?.productId || typeof productChoice.productId !== 'string') return;
    try {
      await deleteProduct(productChoice.productId).unwrap();
      showAlertNotification({
        type: 'success',
        title: 'Xóa sản phẩm thành công',
        message: `Sản phẩm ${productChoice?.['product.code']} đã được xóa`,
      });
    } catch (error) {
      console.log('error', error);
      showAlertNotification({
        type: 'error',
        title: 'Xóa sản phẩm tất bại',
        message: `Sản phẩm ${productChoice?.['product.code']} xóa thất bại`,
      });
    } finally {
      setIsDeleteModalOpen(false);
      setChoiceProduct(null);
    }
  };

  const wrappedHandlers = {
    handleDelete,
    confirmDelete: async () => {
      setIsPerformingOperation(true);
      try {
        await confirmDelete();
      } finally {
        setIsPerformingOperation(false);
      }
    },
  };

  const handleCopy = async (product: ProductColumn): Promise<void> => {
    try {
      setIsInitalCopy(true);
      setModalOpen(true);

      await createcode({ code: 'SP' }).unwrap();
      const response = await triggerGetByIdProduct({
        productId: product.productId,
        versionId: product.key,
        query: {
          include:
            'product.productVersions|productAttributes.attribute|productUnits.unit|status|productImages.image|currencyUnit|baseUnit|productSalePrices|brand|category|product.productSuppliers.supplier.baseUser',
        },
      }).unwrap();
      if (response?.data) {
        if (response.data?.status?.name === 'Active') {
          setIsPublic(true);
        }
        setStatusToCopy(response.data?.status?.name || 'Active');
        setNameData({
          categoryName: response.data.category?.name || '',
          brandName: response.data.brand?.name || '',
          baseUnitName: response.data.baseUnit?.name || '',
          currencyUnitName: response.data.currencyUnit?.name || '',
        });
        initializeFormData(form, response.data);
        if (response.data.productUnits && Array.isArray(response.data.productUnits)) {
          const unitsWithLabels = response.data.productUnits.map((unit) => ({
            ...unit,
            label: unit?.unit?.name,
            checked: true,
          }));
          setInitialProductUnits(unitsWithLabels);
        }

        if (response.data.productAttributes && Array.isArray(response.data.productAttributes)) {
          const attributesWithLabels = response.data.productAttributes.map((attr) => ({
            ...attr,
            title: attr?.attribute?.name,
            surcharge: attr?.salePrice || 0,
          }));
          setInitialProductAttributes(attributesWithLabels);
        }

        if (response.data.productSalePrices && Array.isArray(response.data.productSalePrices)) {
          setInitialProductSalePrices(response.data.productSalePrices);
        }
        if (response.data.productImages && response.data.productImages.length > 0) {
          const sortedImages = [...response.data.productImages].sort((a, b) => a.order - b.order).map((image) => image);
          form.setFieldValue('productImages', sortedImages);
          setInitialImage(sortedImages[0]?.image?.filePath || '');
          console.log('sortedImages', sortedImages[0].image);
        }
        if (response.data.product?.productSuppliers && Array.isArray(response.data.product?.productSuppliers)) {
          setInitialProductSuppliers(
            (response.data.product.productSuppliers.map((supplier) => supplier?.supplier) as Supplier[]) || [],
          );
        }
      }
      setIsInitalCopy(false);
    } catch (error) {
      console.error('Failed to copy product:', error);
    }
  };
  useEffect(() => {
    if (onGetFilteredQuerySetter) {
      onGetFilteredQuerySetter(setFilteredQuery);
    }
  }, [onGetFilteredQuerySetter]);
  const onConfirmCopy = async () => {
    try {
      const productImages = form.getFieldValue('productImages') || [];
      const imagesInfo = productImages.map((productImage: ProductImage) => productImage.image);
      const payload: ProductPayload = {
        code: code || '',
        name: form.getFieldValue('name'),
        categoryId: form.getFieldValue('category'),
        statusId: draftId,
        costPrice: Number(form.getFieldValue('costPrice')),
        salePrice: Number(form.getFieldValue('salePrice')),
        dimension: form.getFieldValue('dimension'),
        color: form.getFieldValue('color'),
        sizeSpec: form.getFieldValue('sizeSpec'),
        description: form.getFieldValue('describeProduct'),
        baseUnitId: form.getFieldValue('baseUnitId'),
        brandId: form.getFieldValue('brand'),
        currencyUnitId: form.getFieldValue('currencyUnitId'),
        minQuantity: Number(form.getFieldValue('min')),
        maxQuantity: Number(form.getFieldValue('max')),
        barcode: generateRandomBarcode(),
        density: Number(form.getFieldValue('density')),
        productAttributes: form.getFieldValue('productAttributes').map((attr: Attribute) => ({
          attributeId: attr.attributeId,
          salePrice: Number(attr.salePrice),
          note: attr.note || '',
          order: attr.order,
        })),
        productUnits: form.getFieldValue('productUnits').map((unit: Unit) => ({
          unitId: unit.unitId,
          convertValue: Number(unit.convertValue),
          costPrice: Number(unit.costPrice),
          salePrice: Number(unit.salePrice),
          order: unit.order,
        })),
        productSalePrices: initialProductSalePrices.map((salePrice) => ({
          minQuantity: Number(salePrice.minQuantity),
          maxQuantity: Number(salePrice.maxQuantity),
          note: salePrice.note,
          salePrice: Number(salePrice.salePrice),
        })),
        productSuppliers: form.getFieldValue('productSuppliers'),
        imagesInfo,
        isPublishing: isPublic,
        isActive: form.getFieldValue('isActive'),
      };
      const res = await createProduct(payload).unwrap();

      if (res.success) {
        showAlertNotification({
          type: 'success',
          title: 'Sao chép sản phẩm thành công',
          message: `Bạn có thể chỉnh sửa và công khai sản phẩm này.`,
        });
        setModalOpen(false);
        navigate(`/items/products/detail-product/${res.data?.productId}/${res.data.id}`);
        form.resetFields();
      }
    } catch (error) {
      showAlertNotification({
        type: 'error',
        title: 'Sao chép sản phẩm thất bại',
        message: `Sao chép sản phẩm thất bại.`,
      });
      console.error('Lỗi tạo sản phẩm:', error);
    }
  };

  const onCancelCopy = async () => {
    setModalOpen(false);
    form.resetFields();
  };

  const handlePagination = (query: QueryParams) => {
    try {
      const columnEqs = ['product.quantity', 'salePrice', 'costPrice', 'version', 'isActive'];
      const columnArray = ['category.name', 'status.id'];

      const filterQuery = buildFullParams(
        {
          ...query,
          isActive: true,
          searchKey: 'name|color|dimension|sizeSpec|product.code',
          searchValue: query.searchValue || '',
          searchField: 'fullName',
        },
        columnEqs,
        columnArray,
      );
      setFilteredQuery((prev) => ({
        ...prev,
        ...filterQuery,
      }));
      const newQuery = {
        searchValue: query.searchValue ? query.searchValue : filteredQuery.searchValue || '',
        ...filterQuery,
      } as unknown as QueryParams;
      onParentFilter?.(newQuery);
    } catch (error) {
      console.log('handlePagination: ', error);
    }
  };
  return (
    <div className='bg-[var(--textWhite)] product-table'>
      <TableCommon<ProductColumn>
        columns={newColumns}
        dataSource={products}
        defaultPageSize={filteredQuery.size as number}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCopy={handleCopy}
        tableProps={{
          scroll: { x: 'max-content' },
          size: 'large',
          className: 'shadow-sm',
          style: {
            minHeight: 'calc(100vh - 385px)',
          },
          loading: isLoading || triggerLoading,
          locale: {
            emptyText: (
              <div
                style={{
                  height: 'calc(100vh - 385px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 400,
                }}
              >
                Sản phẩm chưa được tạo, vui lòng click vào nút thêm sản phẩm...
              </div>
            ),
          },
        }}
        count={data?.data?.count}
        onPaginationChange={handlePagination}
        currentPage={Number(filteredQuery.page)}
        searchColumnDefault={parseSimpleFilter(tableState?.filter)}
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onCancel={cancelDelete}
        onConfirm={wrappedHandlers.confirmDelete}
        title='Xác nhận xóa sản phẩm'
        subtitle={
          <div>
            Bạn có chắc chắn muốn xóa sản phẩm
            <span className='font-semibold'> {productChoice?.name}</span> không?
            <br />
            Nếu có hãy chọn vào nút xóa. Tất cả thao tác của bạn sẽ không được hoàn tác
          </div>
        }
        icon='delete'
        confirmText='Xóa'
        confirmLoading={isPerformingOperation}
      />
      <ModalConfirm
        open={modalOpen}
        setOpen={setModalOpen}
        onConfirm={onConfirmCopy}
        isLoading={createLoading || triggerLoading || isInitalCopy}
        isDetail={false}
        onCancel={onCancelCopy}
        form={form}
        productAttributes={initialProductAttributes}
        productUnits={initialProductUnits}
        productSalePrices={initialProductSalePrices}
        suppliers={initalProductSuppliers}
        headerLabel='XÁC NHẬN SAO CHÉP SẢN PHẨM'
        headerContent='Bạn đang tạo bản sao từ sản phẩm đã chọn. Vui lòng kiểm tra lại.'
        saveButtonLabel='Xác nhận sao chép sản phẩm'
        categoryName={nameData.categoryName}
        brandName={nameData.brandName}
        currencyUnitName={nameData.currencyUnitName}
        baseUnitName={nameData.baseUnitName}
        statusToCopy={statusToCopy}
        imageMain={initialImage}
      />
    </div>
  );
};

export default ProductTable;
